import { ref, watch, type Ref } from 'vue';
import { IMnemonicLikeService } from '@lingvo-news/contracts';
import { getVueEnv } from '../infrastructure/vue-env';

export type MnemonicRating = 1 | -1 | 0;

export function useMnemonicLikes(
	wordIds: Ref<string[]>,
	resolveUserId: () => Promise<string | null>
) {
	const ratingsByWordId = ref<Map<string, number>>(new Map());
	const pendingIds = ref<Set<string>>(new Set());
	const isAuthenticated = ref(false);

	watch(
		wordIds,
		async (ids) => {
			const uniqueIds = [...new Set(ids)].filter(Boolean);
			if (uniqueIds.length === 0) {
				ratingsByWordId.value = new Map();
				return;
			}

			const userId = await resolveUserId();
			isAuthenticated.value = !!userId;
			if (!userId) return;

			try {
				const env = getVueEnv();
				const service = env.get(IMnemonicLikeService);
				const response = await service.getLikes({ wordIds: uniqueIds }, userId);
				const map = new Map<string, number>();
				for (const item of response.items) {
					map.set(item.wordId, item.rating);
				}
				ratingsByWordId.value = map;
			} catch (err) {
				console.warn('Failed to load mnemonic likes', err);
			}
		},
		{ immediate: true }
	);

	const getRating = (wordId: string): number => ratingsByWordId.value.get(wordId) ?? 0;
	const isPending = (wordId: string): boolean => pendingIds.value.has(wordId);

	const setRating = async (wordId: string, rating: MnemonicRating): Promise<void> => {
		if (pendingIds.value.has(wordId)) return;

		const userId = await resolveUserId();
		isAuthenticated.value = !!userId;
		if (!userId) return;

		const next = new Set(pendingIds.value);
		next.add(wordId);
		pendingIds.value = next;

		try {
			const env = getVueEnv();
			const service = env.get(IMnemonicLikeService);
			const currentRating = getRating(wordId);
			const newRating: MnemonicRating = currentRating === rating ? 0 : rating;

			await service.setLike({ wordId, rating: newRating }, userId);

			const updated = new Map(ratingsByWordId.value);
			if (newRating === 0) {
				updated.delete(wordId);
			} else {
				updated.set(wordId, newRating);
			}
			ratingsByWordId.value = updated;
		} catch (err) {
			console.error('Failed to set mnemonic like', err);
		} finally {
			const cleaned = new Set(pendingIds.value);
			cleaned.delete(wordId);
			pendingIds.value = cleaned;
		}
	};

	return { getRating, isPending, setRating, isAuthenticated };
}
